import { useEffect, useRef, useState } from "react";
import { Camera, Upload, X, RefreshCw } from "lucide-react";

// Returns base64 (no prefix) + preview data url via onCapture(base64, dataUrl)
export function ImageCapture({ onImage }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const streamRef = useRef(null);

  const startCamera = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false,
      });
      streamRef.current = stream;
      setStreaming(true); // render <video> first; attach happens in the effect below
    } catch (e) {
      setErr("Camera not available or permission denied. Upload a photo instead.");
    }
  };

  // Attach the stream once the <video> element is actually in the DOM.
  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      const v = videoRef.current;
      v.srcObject = streamRef.current;
      v.play().catch(() => {});
    }
  }, [streaming]);

  // Clean up the camera when unmounting.
  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setStreaming(false);
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { setErr("Camera still loading — wait a moment and tap Capture again."); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setPreview(dataUrl);
    stopCamera();
    onImage(dataUrl.split(",")[1], dataUrl);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); onImage(reader.result.split(",")[1], reader.result); };
    reader.readAsDataURL(f);
  };

  const reset = () => { setPreview(null); onImage(null, null); };

  return (
    <div className="pm-card p-4" data-testid="image-capture">
      {preview ? (
        <div className="relative">
          <img src={preview} alt="capture" className="w-full max-h-80 object-contain" data-testid="capture-preview" />
          <button onClick={reset} data-testid="capture-reset" className="pm-btn-ghost mt-3 flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Use a different photo
          </button>
        </div>
      ) : streaming ? (
        <div>
          <div className="relative bg-black" style={{ minHeight: "240px" }}>
            <video ref={videoRef} className="w-full max-h-80 object-contain" style={{ minHeight: "240px" }} autoPlay playsInline muted />
            <div className="scan-line" />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={snap} className="pm-btn flex items-center gap-2" data-testid="capture-snap">
              <Camera className="w-4 h-4" /> Capture
            </button>
            <button onClick={stopCamera} className="pm-btn-ghost flex items-center gap-2" data-testid="capture-stop">
              <X className="w-4 h-4" /> Stop
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={startCamera} className="pm-btn flex items-center gap-2 justify-center" data-testid="open-camera-btn">
            <Camera className="w-4 h-4" /> Use camera
          </button>
          <button onClick={() => fileRef.current?.click()} className="pm-btn-ghost flex items-center gap-2 justify-center" data-testid="upload-btn">
            <Upload className="w-4 h-4" /> Upload a photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="file-input" />
        </div>
      )}
      {err && <p className="text-xs mt-2" style={{ color: "var(--warning)" }}>{err}</p>}
    </div>
  );
}
